import CONSTANTS from 'src/utils/constants';
import {
    CDTSegmentType,
    ColumnDataType,
    ColumnDef,
    ColumnSourceType,
    SearchSegmentDataTableRowsResponse,
    SegmentPeriodInfo,
    SegmentPullCriteria,
    SegmentTableDefinition,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import ReactTableFilter from 'src/models/common/ReactTableFilter';

/** Rows per page in the detail data-viewer; mirrors the CLI custom-data-tables records grid. */
export const SEGMENT_DETAIL_PAGE_SIZE = 50;

/** A page of segment rows plus the client-derived `pagesCount` a numbered pager needs. */
export type SegmentRowsPage = SearchSegmentDataTableRowsResponse & { pagesCount: number };

/** In-progress config for the single column currently being added. */
export interface SegmentColumnDraft {
    displayName?: string;
    sourceType?: ColumnSourceType; // CR2 builds MANUAL | PULL_GL only
    dataType?: ColumnDataType;
    currency?: string;
    pullCriteria?: SegmentPullCriteria;
}

/**
 * State for the Create Segment Data Table wizard. Resettable as a unit on open/close.
 * `columns` array order IS `value_order` (immutable after create). `columnEditor` is the
 * add-column sub-form; when absent the column list is shown.
 */
export interface CreateWizardState {
    visible: boolean;
    currentStep: number; // 1 = meta, 2 = columns
    tableName: string; // seeded with the CDT name prefix
    segmentType?: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    columns: ColumnDef[];
    // True while the column-type chooser is shown (before an editor opens). Held here — not in local
    // component state — so the whole flow resets with the slice on open/close.
    choosing: boolean;
    columnEditor?: { draft: SegmentColumnDraft };
    creating: boolean;
    createError: string;
}

/**
 * State for the detail (data-viewer) route: the table's definition + the period being viewed and the
 * current page/sort of its rows. Row data itself is the reducer-held `searchResult` (set by the search
 * hook), mirroring the CLI records view. `periods` comes from GetSegmentPeriods (empty until that op
 * ships); `selectedPeriod` defaults to the latest.
 */
export interface SegmentDetailState {
    definition?: SegmentTableDefinition;
    periods: SegmentPeriodInfo[];
    selectedPeriod?: string;
    page: number; // 1-based, matching the backend's numbered paging
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    // Per-column "filter rows" values (react-table shape); built into the search filterExpression.
    filters: ReactTableFilter[];
    searchResult?: SegmentRowsPage;
}

/**
 * State for the Segment Data Tables feature. `refreshToken` drives the list re-fetch (bumped after
 * a create); `createWizard` holds the create flow; `detail` holds the data-viewer route. List/row data
 * itself lives in the service hook tuples. The list tab and the detail route are separate mounts, so
 * each gets its own reducer instance — the slices never coexist in one live store.
 */
export interface SegmentTablesState {
    refreshToken: number;
    createWizard: CreateWizardState;
    detail: SegmentDetailState;
}

export const initialCreateWizardState: CreateWizardState = {
    visible: false,
    currentStep: 1,
    tableName: CONSTANTS.CUSTOM_DATA_TABLE_PREFIX,
    columns: [],
    choosing: false,
    creating: false,
    createError: '',
};

// Shared module constant: OPEN/CLOSE_CREATE_WIZARD spread it, so its `columns` array reference is
// handed to every wizard. Freeze the object and that array so an accidental in-place mutation fails
// loudly instead of silently leaking across wizard sessions. Every reducer path already builds new
// arrays, so this only guards against future edits.
Object.freeze(initialCreateWizardState.columns);
Object.freeze(initialCreateWizardState);

export const initialDetailState: SegmentDetailState = {
    periods: [],
    page: 1,
    pageSize: SEGMENT_DETAIL_PAGE_SIZE,
    filters: [],
};
// Frozen like the wizard state: every reducer path builds new objects, so this only guards against a
// future in-place mutation of the shared initial slice.
Object.freeze(initialDetailState.periods);
Object.freeze(initialDetailState.filters);
Object.freeze(initialDetailState);

export const initialState: SegmentTablesState = {
    refreshToken: 0,
    createWizard: initialCreateWizardState,
    detail: initialDetailState,
};
Object.freeze(initialState);
